<template>
  <div class="relative overflow-auto">
    <!-- Loading overlay -->
    <div v-if="props.isLoading" class="max-h-full absolute inset-0 bg-white bg-opacity-90 z-10">
      <div class="flex items-center max-h-52 justify-center pt-52">
        <Loader2 class="w-4 h-4 mr-2 animate-spin" />
        <span class="text-sm text-gray-600 font-medium">Loading data...</span>
      </div>
    </div>

    <table
      class="min-w-full divide-y divide-gray-200 rounded-lg overflow-hidden table-fixed w-full"
    >
      <thead class="bg-white">
        <tr>
          <th scope="col" class="w-8 pl-2 py-3 text-center text-xs font-normal text-gray-500">#</th>
          <th scope="col" class="py-3 pr-2 text-xs font-normal text-gray-500 w-12 text-center">
            Section
          </th>
          <th scope="col" class="py-3 text-left text-xs font-normal text-gray-500 max-w-[48rem]">
            Title
          </th>
          <th scope="col" class="py-3 text-center text-xs font-normal text-gray-500 w-10">
            Details
          </th>
          <th scope="col" class="py-3 text-center text-xs font-normal text-gray-500 w-16">
            Points
          </th>
          <th scope="col" class="py-3 text-left text-xs font-normal text-gray-500 w-48">Status</th>
          <th scope="col" class="relative py-3 w-10">
            <span class="sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      <tbody class="bg-white divide-y divide-gray-200">
        <template v-for="(item, index) in curriculumItems" :key="item.id">
          <tr
            @click="toggleExpand(item.id)"
            class="hover:bg-gray-50 cursor-pointer transition-all duration-200"
          >
            <td class="py-4 w-8 pl-2 text-sm text-gray-400 text-center">{{ index + 1 }}</td>
            <td class="py-2 pr-2 whitespace-normal text-sm text-gray-400 text-center">
              <div class="flex justify-center items-center">
                <HoverCard
                  v-if="
                    item.path_step.task.section?.name &&
                    getSectionIcon(item.path_step.task.section?.name)
                  "
                >
                  <HoverCardTrigger>
                    <component
                      :is="getSectionIcon(item.path_step.task.section?.name)"
                      class="w-5 h-5 text-gray-500 cursor-pointer"
                    />
                  </HoverCardTrigger>
                  <HoverCardContent class="w-auto p-2 text-sm">
                    {{ item.path_step.task.section?.name }}
                  </HoverCardContent>
                </HoverCard>
              </div>
            </td>
            <td class="py-4 whitespace-normal text-sm text-gray-900 truncate">
              <a
                v-if="item.path_step.task.url && isUrl(item.path_step.task.url)"
                :href="item.path_step.task.url"
                target="_blank"
                class="text-gray-900 hover:text-cosmicPurple inline-flex items-center transition-all duration-200"
                @click.stop="handleOpenTask(item, $event)"
              >
                <span class="truncate">{{ item.path_step.task.name }}</span>
                <ExternalLink class="w-4 h-4 ml-1 shrink-0 text-cosmicPurple" />
                <span
                  v-if="!item.path_step.is_required"
                  class="text-xs bg-yellow-100 text-yellow-800 ml-2 px-2 py-0.5 rounded-full"
                  >Optional</span
                >
              </a>
              <template v-else>
                <span class="truncate">{{ item.path_step.task.name }}</span>
              </template>
            </td>

            <td class="py-4 whitespace-normal text-sm text-center">
              <FileText v-if="item.path_step.task.details" class="w-5 h-5 text-gray-500 mx-auto" />
            </td>

            <td class="py-4 whitespace-normal text-sm text-center flex items-center justify-center">
              <span
                v-if="item.path_step.points > 0"
                class="text-cosmicPurple bg-cosmicPurple/20 px-2 py-1 rounded-full min-w-7 min-h-7 text-center mt-0.5"
                >{{ item.path_step.points }}</span
              >
            </td>

            <td class="py-4 whitespace-normal text-sm" @click.stop>
              <Select
                v-model="itemStatuses[item.id]"
                @update:model-value="
                  (newStatus) => updateTaskStatus(item.id, newStatus as TaskStatus)
                "
                :disabled="updatingTask === item.id"
              >
                <SelectTrigger
                  class="w-full text-xs h-8 max-w-40 transition-all duration-200"
                  :class="[
                    item.status === 'DONE'
                      ? 'border-none text-lime-900 bg-lime-300 hover:bg-lime-200 '
                      : item.status === 'IN-PROGRESS'
                        ? 'border-none text-cosmicPurple bg-cosmicPurple/20 hover:bg-cosmicPurple/30'
                        : 'border-[1px] border-gray-300 text-gray-500'
                  ]"
                >
                  <div class="flex items-center gap-2 mx-auto">
                    <Loader2 v-if="updatingTask === item.id" class="w-4 h-4 animate-spin" />
                    <template v-else>
                      <CheckCircle v-if="item.status === 'DONE'" class="w-4 h-4" />
                      <Clock v-else-if="item.status === 'IN-PROGRESS'" class="w-4 h-4" />
                      <CircleDot v-else class="w-4 h-4" />
                    </template>
                    <SelectValue :placeholder="formatStatus(item.status)" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="TO START">
                      <div class="flex items-center gap-2">
                        <CircleDot class="w-4 h-4" />
                        <span>Incomplete</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="IN-PROGRESS">
                      <div class="flex items-center gap-2">
                        <Clock class="w-4 h-4" />
                        <span>In Progress</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="DONE">
                      <div class="flex items-center gap-2">
                        <CheckCircle class="w-4 h-4" />
                        <span>Completed</span>
                      </div>
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </td>
            <td class="py-4 text-sm text-gray-500">
              <div class="text-gray-400">
                <ChevronDown v-if="expandedItem !== item.id" class="w-5 h-5 mx-auto" />
                <ChevronUp v-else class="w-5 h-5 mx-auto" />
              </div>
            </td>
          </tr>
          <tr v-if="expandedItem === item.id" class="expanded-row">
            <td colspan="7" class="bg-lightGray p-2 expanded-cell">
              <TaskExpanded
                :item="item"
                :is-notes-loading="props.isNotesLoading"
                :is-updating="props.isUpdating"
                :personal-notes="props.personalNotes[item.id] ?? null"
                :is-manage-view="isManageView"
                :handle-open-task="handleOpenTask"
                @save-notes="(notes) => emit('save-notes', { id: item.id, notes })"
                @notes-interacted="() => emit('notes-interacted', item.id)"
              />
            </td>
          </tr>
        </template>
        <!-- Empty state -->
        <tr v-if="!props.isLoading && curriculumItems.length === 0">
          <td colspan="7" class="py-16 text-center text-sm text-gray-500">
            No tasks for this week.
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Task Review Modal -->
    <TaskReview
      v-if="reviewingTask"
      :path-step-id="reviewingTask.path_step.id"
      :task-id="reviewingTask.path_step.task.id"
      :progress-id="reviewingTask.id"
      :path-step-name="reviewingTask.path_step.task.name"
      :on-complete="handleReviewComplete"
      :on-cancel="handleReviewCancel"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, reactive, watch } from 'vue'
import { useRoute } from 'vue-router' // Import useRoute
import {
  ChevronDown,
  ChevronUp,
  Loader2,
  ExternalLink,
  FileText,
  CheckCircle,
  Clock,
  CircleDot
} from 'lucide-vue-next'
import type { PersonalNote, TableProgressItem, TaskStatus } from '@/types/curriculum' // Keep PersonalNote if needed
import TaskExpanded from './TaskExpanded.vue'
import TaskReview from './TaskReview.vue'
import { isUrl } from '@/utils/isUrl'
import { getSectionIcon } from '@/const/curriculumSections'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { useCurriculumStore } from '@/stores/curriculum'
import { useExpeditionsStore } from '@/stores/expeditions'
import { useToast } from 'primevue/usetoast'
import { sortCurriculumItems } from '@/utils/curriculumSorting'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

// Define Props using the flexible type
const props = defineProps<{
  items: TableProgressItem[]
  isLoading: boolean
  isNotesLoading: boolean
  isUpdating: boolean
  personalNotes: Record<number, PersonalNote | null>
}>()

// Define Emits
const emit = defineEmits<{
  (e: 'toggle-expand', id: number): void
  (e: 'save-notes', payload: { id: number; notes: string }): void
  (e: 'notes-interacted', id: number): void
  (e: 'update-status', payload: { id: number; status: TaskStatus }): void
}>()

const route = useRoute() // Initialize route
const curriculumStore = useCurriculumStore()
const expeditionsStore = useExpeditionsStore()
const toast = useToast()
const updatingTask = ref<number | null>(null)
const reviewingTask = ref<TableProgressItem | null>(null)

// Check if the current view is a manage view
const isManageView = computed(() => route.path.startsWith('/manage/'))

const expandedItem = ref<number | null>(null)

const curriculumItems = computed<TableProgressItem[]>(() => {
  return sortCurriculumItems(props.items || [])
})

// Create a reactive map to store statuses
const itemStatuses = reactive<Record<number, TaskStatus>>({})

// Initialize statuses when items change
watch(
  () => props.items,
  (newItems) => {
    newItems.forEach((item) => {
      itemStatuses[item.id] = item.status
    })
  },
  { immediate: true }
)

const toggleExpand = async (id: number) => {
  expandedItem.value = expandedItem.value === id ? null : id
  if (expandedItem.value === id) {
    emit('toggle-expand', id)
  }
}

// Function to handle opening a task link with status update if needed
const handleOpenTask = async (item: TableProgressItem, event?: Event | undefined) => {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }

  const taskUrl = item.path_step.task.url
  if (!taskUrl || !isUrl(taskUrl)) return

  // Only update status if it's "TO START"
  if (item.status === 'TO START') {
    updatingTask.value = item.id

    try {
      // Update to IN-PROGRESS first
      await updateTaskStatus(item.id, 'IN-PROGRESS')

      // Open the link after successful update
      window.open(taskUrl, '_blank')
    } catch (error) {
      console.error('Error updating status before opening link:', error)

      // Still open the link even if update fails
      window.open(taskUrl, '_blank')
    } finally {
      updatingTask.value = null
    }
  } else {
    // If not "TO START", just open the link
    window.open(taskUrl, '_blank')
  }
}

// Function to update task status
const updateTaskStatus = async (id: number, status: TaskStatus | null) => {
  if (!status) return // Early return if status is null

  const item = curriculumItems.value.find((item) => item.id === id)!
  if (status === 'DONE' && item.path_step.task.review_required !== false) {
    showReviewModal(item)
    return
  }

  if (!expeditionsStore.latestExpeditionId) {
    console.error('Cannot update status, missing expedition ID')
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Cannot update status: Missing expedition ID.',
      life: 3000
    })
    return Promise.reject('Missing expedition ID')
  }

  updatingTask.value = id

  try {
    const weekIdFromRoute = route.query.week ? String(route.query.week) : null
    await curriculumStore.updateProgressStatus(
      id,
      status,
      expeditionsStore.latestExpeditionId,
      weekIdFromRoute
    )

    // Update the local status
    itemStatuses[id] = status

    // Emit the update event so parent component can update its data
    emit('update-status', { id, status })

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Progress status updated successfully',
      life: 3000
    })

    return Promise.resolve()
  } catch (error) {
    console.error('Error updating status:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update progress status',
      life: 5000
    })

    // Revert the local status on error
    const originalItem = curriculumItems.value.find((item) => item.id === id)
    if (originalItem) {
      itemStatuses[id] = originalItem.status
    }

    return Promise.reject(error)
  } finally {
    updatingTask.value = null
  }
}

// --- Keep display logic functions ---
const getStatusClass = (status: string) => {
  switch (status) {
    case 'DONE':
      return 'bg-lime-200 text-lime-800'
    case 'IN-PROGRESS':
      return 'bg-cosmicPurple/20 text-cosmicPurple'
    case 'TO START':
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const formatStatus = (status: string) => {
  switch (status) {
    case 'TO START':
      return 'Incomplete'
    case 'IN-PROGRESS':
      return 'In Progress'
    case 'DONE':
      return 'Completed'
    default:
      return status
  }
}

// Function to show review modal
const showReviewModal = (item: TableProgressItem) => {
  reviewingTask.value = item
}

// Handle review completion
const handleReviewComplete = () => {
  if (reviewingTask.value) {
    // Update the local status
    itemStatuses[reviewingTask.value.id] = 'DONE'
    emit('update-status', { id: reviewingTask.value.id, status: 'DONE' })
  }
  reviewingTask.value = null
}

// Handle review cancellation
const handleReviewCancel = () => {
  reviewingTask.value = null
}
</script>

<style scoped>
.expanded-row {
  transition: all 0.3s ease-in-out;
}

.expanded-cell {
  animation: expand 0.3s ease-in-out forwards;
  overflow: hidden;
}

@keyframes expand {
  from {
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
  to {
    max-height: 1000px; /* Large enough to fit content */
    padding: 0.5rem;
  }
}
</style>
